/**
 * The DOM contract for a rendered tile.
 *
 * `TileComponent` marks every tile it renders with this class and id attribute, and several
 * features find tiles by querying for them: keyboard navigation between siblings, drag/pick-up
 * targeting, and visibility logging. A model-based alternative was tried for keyboard nav and gave
 * the wrong order in practice, so the DOM remains the source of truth for "which tiles are on
 * screen, and where".
 *
 * That makes the class name load-bearing rather than cosmetic. Both sides of the contract are
 * defined here so a rename reaches all of them at once, and `tile-component.test.tsx` asserts that
 * what `TileComponent` renders is still what these queries look for.
 */
export const kTileClass = "tool-tile";
export const kTileIdAttr = "data-tool-id";
/** Matches any rendered tile carrying an id. */
export const kTileSelector = `.${kTileClass}[${kTileIdAttr}]`;
/** Matches the one rendered tile with this id. */
export function tileSelector(tileId) {
    return `.${kTileClass}[${kTileIdAttr}="${tileId}"]`;
}
/** The attributes `TileComponent` puts on a tile's element, for the query helpers to find. */
export function tileDomAttributes(tileId) {
    return { [kTileIdAttr]: tileId };
}
/**
 * Every rendered tile under `root`, in document order. Tiles nested inside a container tile are
 * included along with the container itself; use `getContainingTileNode` to tell them apart.
 */
export function getTileNodes(root) {
    return Array.from(root.querySelectorAll(kTileSelector));
}
export function getTileNode(root, tileId) {
    var _a;
    return (_a = root.querySelector(tileSelector(tileId))) !== null && _a !== void 0 ? _a : undefined;
}
export function getTileIdFromNode(node) {
    return node.dataset.toolId;
}
/** The container tile's element, for a tile rendered inside one; undefined for a top-level tile. */
export function getContainingTileNode(node) {
    var _a, _b;
    return (_b = (_a = node.parentElement) === null || _a === void 0 ? void 0 : _a.closest(kTileSelector)) !== null && _b !== void 0 ? _b : undefined;
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoidGlsZS1kb20uanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi8uLi9zcmMvY29tcG9uZW50cy90aWxlcy90aWxlLWRvbS50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFBQTs7Ozs7Ozs7Ozs7O0dBWUc7QUFFSCxNQUFNLENBQUMsTUFBTSxVQUFVLEdBQUcsV0FBVyxDQUFDO0FBQ3RDLE1BQU0sQ0FBQyxNQUFNLFdBQVcsR0FBRyxjQUFjLENBQUM7QUFFMUMsZ0RBQWdEO0FBQ2hELE1BQU0sQ0FBQyxNQUFNLGFBQWEsR0FBRyxJQUFJLFVBQVUsSUFBSSxXQUFXLEdBQUcsQ0FBQztBQUU5RCxrREFBa0Q7QUFDbEQsTUFBTSxVQUFVLFlBQVksQ0FBQyxNQUFjO0lBQ3pDLE9BQU8sSUFBSSxVQUFVLElBQUksV0FBVyxLQUFLLE1BQU0sSUFBSSxDQUFDO0FBQ3RELENBQUM7QUFFRCw4RkFBOEY7QUFDOUYsTUFBTSxVQUFVLGlCQUFpQixDQUFDLE1BQWM7SUFDOUMsT0FBTyxFQUFFLENBQUMsV0FBVyxDQUFDLEVBQUUsTUFBTSxFQUFFLENBQUM7QUFDbkMsQ0FBQztBQUVEOzs7R0FHRztBQUNILE1BQU0sVUFBVSxZQUFZLENBQUMsSUFBZ0I7SUFDM0MsT0FBTyxLQUFLLENBQUMsSUFBSSxDQUFDLElBQUksQ0FBQyxnQkFBZ0IsQ0FBYyxhQUFhLENBQUMsQ0FBQyxDQUFDO0FBQ3ZFLENBQUM7QUFFRCxNQUFNLFVBQVUsV0FBVyxDQUFDLElBQWdCLEVBQUUsTUFBYzs7SUFDMUQsT0FBTyxNQUFBLElBQUksQ0FBQyxhQUFhLENBQWMsWUFBWSxDQUFDLE1BQU0sQ0FBQyxDQUFDLG1DQUFJLFNBQVMsQ0FBQztBQUM1RSxDQUFDO0FBRUQsTUFBTSxVQUFVLGlCQUFpQixDQUFDLElBQWlCO0lBQ2pELE9BQU8sSUFBSSxDQUFDLE9BQU8sQ0FBQyxNQUFNLENBQUM7QUFDN0IsQ0FBQztBQUVELG9HQUFvRztBQUNwRyxNQUFNLFVBQVUscUJBQXFCLENBQUMsSUFBaUI7O0lBQ3JELE9BQU8sTUFBQSxNQUFBLElBQUksQ0FBQyxhQUFhLDBDQUFFLE9BQU8sQ0FBYyxhQUFhLENBQUMsbUNBQUksU0FBUyxDQUFDO0FBQzlFLENBQUMifQ==